/**
 * First-party analytics beacon.
 *
 * Anonymous by construction: no cookies, no localStorage, no device or user id, and nothing
 * identifying in the payload. It posts a single page key to `/_hub/collect`, which our edge
 * routes to the shared analytics hub; the hub increments an anonymous daily counter and returns
 * 204. The raw IP is hashed with a salt this app never sees and then discarded.
 *
 * `/api/*` is SvcDue's own API, so the beacon lives under `/_hub/*` — a separate CloudFront
 * behaviour that forwards no cookies and no Authorization header (see infra analytics-beacon.tf).
 * A beacon cannot carry a session even by accident.
 *
 * THE ROUTE MAPPING IS A PRIVACY BOUNDARY, not a tidiness exercise. Two paths carry secrets in
 * the URL itself:
 *
 *   /g/:token          — a share token. Anyone holding it can read that garage.
 *   /tools/classcalc/:catalogue — harmless, but still an unbounded value.
 *
 * Sending a raw pathname would write those into a metric name, where they would sit in a
 * datastore, in dashboards, and in anything downstream. So the mapping below is an ALLOWLIST
 * that collapses every parameterised route to a fixed word, and anything unrecognised becomes
 * "other". A new route is invisible until it is added here, which is the safe direction to fail.
 */

const PAGE_BY_PREFIX: ReadonlyArray<readonly [string, string]> = [
  ['/g/', 'garage'], // share token in the URL — never send the token itself
  ['/tools/classcalc', 'classcalc'],
  /**
   * The signed-in garage screen, and it is NOT `garage`.
   *
   * `/g/:token` above has been reporting `garage` since the beacon shipped, and it means "someone
   * opened a shared link" — a public, signed-out visit. Reusing the word here would quietly merge
   * an owner browsing their own cars into that same counter and change what every historical
   * `garage` count meant. `fleet` is the app's own word for the same thing (the Fleet heading on
   * the garage and public screens), so the two stay separate and both stay readable.
   *
   * `/garage/:carId` collapses into this entry by prefix, which is the point: the car id is a
   * tenant-scoped identifier and has no business in a metric name.
   */
  ['/garage', 'fleet'],
  ['/timeline', 'timeline'],
  ['/driver', 'driver'],
  ['/crew', 'crew'],
  ['/account', 'account'],
  ['/admin', 'admin'],
  ['/terms', 'terms'],
  ['/privacy', 'privacy'],
];

/** Collapse a pathname to an allowlisted page key. Never returns anything URL-derived. */
export function pageKeyFor(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'index';
  const match = PAGE_BY_PREFIX.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix));
  return match ? match[1] : 'other';
}

/**
 * Fire-and-forget. `sendBeacon` survives the page being closed and never blocks; the keepalive
 * fetch is the fallback for browsers without it. Every failure is swallowed — analytics must
 * never break the app, and a retry storm on a counter would be worse than a missed count.
 */
function send(page: string): void {
  const body = JSON.stringify({ p: page });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/_hub/collect', new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch('/_hub/collect', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never surface an analytics failure to the user */
  }
}

let lastPage: string | null = null;

/**
 * Record a pageview for a client-side route change.
 *
 * Deduped against the previous key, because several routes collapse to the same page and React
 * Router re-runs effects on every navigation — without this, moving between two `/tools/classcalc`
 * catalogues would count as two views of a page the user never left.
 */
export function trackPageview(pathname: string): void {
  const page = pageKeyFor(pathname);
  if (page === lastPage) return;
  lastPage = page;
  send(page);
}
